import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusCircle, Edit, Trash2, Settings, Zap, Image, FileText, Save, Crown, Users, Coins, Tag, Code, Eye, DollarSign, Calendar, Gift, RefreshCw, ArrowUpDown, Wand2, Layers, Box, Video, Clock, HardDrive, Loader2, Shield, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

import tierService, { TierUpdate } from '../../services/tierService';
import { TierPricingFeaturesManager } from '../../components/admin/TierPricingFeaturesManager';
import { CreditPackageManager } from '../../components/admin/CreditPackageManager';
import { CreditScheduleControl } from '../../components/admin/CreditScheduleControl';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

// Types for our tier system
interface Tier {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  is_active: boolean;
  sort_order: number;
  signup_credits: number;
  monthly_credits: number;
  bonus_credits: number;
  bonus_start_date?: string;
  bonus_end_date?: string;
  bonus_description?: string;
  bonus_is_active: boolean;
  button_text?: string;
  button_url?: string;
  is_button_enabled: boolean;
  icon?: string;
  roi_text_1?: string;
  roi_text_2?: string;
  show_roi_texts: boolean;
  user_count?: number;
  created_at: string;
  updated_at: string;
}

interface TierCreate {
  name: string;
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  signup_credits: number;
  monthly_credits: number;
  bonus_credits: number;
  bonus_start_date?: string;
  bonus_end_date?: string;
  bonus_description?: string;
  bonus_is_active: boolean;
  sort_order: number;
  button_text?: string;
  button_url?: string;
  is_button_enabled: boolean;
  icon?: string;
  roi_text_1?: string;
  roi_text_2?: string;
  show_roi_texts: boolean;
}

interface TierLimit {
  id: string;
  tier_id: string;
  feature_category_id: string;
  feature_key: string;
  feature_display_name: string;
  feature_description?: string;
  limit_value: number;
  reset_period: 'monthly' | 'daily' | 'never';
  is_active: boolean;
}

// Tier Limits Form Component
const TierLimitsForm: React.FC<{
  tierLimits: TierLimit[];
  onSave: (limits: Record<string, { limit_value: number; reset_period: string; is_active: boolean }>) => void;
  isLoading: boolean;
}> = ({ tierLimits, onSave, isLoading }) => {
  const [limits, setLimits] = useState<Record<string, { limit_value: number; reset_period: string; is_active: boolean }>>({});

  // Initialize limits from props
  React.useEffect(() => {
    console.log('TierLimitsForm received tierLimits:', tierLimits);
    const initialLimits: Record<string, { limit_value: number; reset_period: string; is_active: boolean }> = {};

    // Define the standard social media limits that should exist for all tiers
    const standardLimits = [
      { key: 'connected_accounts', defaultValue: 2, resetPeriod: 'never' },
      { key: 'monthly_posts', defaultValue: 10, resetPeriod: 'monthly' },
      { key: 'scheduled_queue', defaultValue: 5, resetPeriod: 'never' },
      { key: 'team_members', defaultValue: 1, resetPeriod: 'never' },
      { key: 'storage_gb', defaultValue: 1, resetPeriod: 'never' },
    ];

    // First, add standard limits with defaults
    standardLimits.forEach(({ key, defaultValue, resetPeriod }) => {
      initialLimits[key] = {
        limit_value: defaultValue,
        reset_period: resetPeriod,
        is_active: true
      };
    });

    // Then override with existing database values
    tierLimits.forEach(limit => {
      initialLimits[limit.feature_key] = {
        limit_value: limit.limit_value,
        reset_period: limit.reset_period,
        is_active: limit.is_active
      };
    });

    console.log('Final initialLimits:', initialLimits);
    setLimits(initialLimits);
  }, [tierLimits]);

  const updateLimit = (featureKey: string, field: string, value: any) => {
    setLimits(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(limits);
  };

  const getFeatureIcon = (featureKey: string) => {
    switch (featureKey) {
      case 'connected_accounts':
      case 'Connected Social Accounts':
        return <Users className="h-4 w-4" />;
      case 'team_members':
      case 'Team Members':
        return <Users className="h-4 w-4" />;
      case 'monthly_posts':
      case 'posts_per_month':
        return <FileText className="h-4 w-4" />;
      case 'scheduled_queue':
      case 'scheduled_posts':
        return <Clock className="h-4 w-4" />;
      case 'storage_gb':
      case 'Media Storage (GB)':
        return <HardDrive className="h-4 w-4" />;
      case 'analytics_days':
        return <TrendingUp className="h-4 w-4" />;
      case 'Image Generation (Basic)':
      case 'Image Generation with Brandify':
        return <Wand2 className="h-4 w-4" />;
      default:
        return <Zap className="h-4 w-4" />;
    }
  };

  const getFeatureDisplayName = (featureKey: string) => {
    const names: Record<string, string> = {
      // Social Media Limits (enforced)
      'connected_accounts': 'Connected Social Accounts',
      'monthly_posts': 'Monthly Posts',
      'scheduled_queue': 'Scheduled Posts Queue',
      'team_members': 'Team Members',
      'storage_gb': 'Media Storage (GB)',
      
      // Legacy names for compatibility
      'Connected Social Accounts': 'Connected Social Accounts',
      'Team Members': 'Team Members',
      'Social Analytics': 'Social Analytics Access',
      
      // AI Task Types (credit-based only - no tier limits)
      'text_to_image': 'Image Generation (Basic) [Credit-Based]',
      'image_processing': 'Image Generation with Brandify [Credit-Based]',
      'AI-Assisted Post Creation': 'AI-Assisted Post Creation [Credit-Based]',
      'image_to_3d': 'Image to 3D Model Generation [Credit-Based]',
      'three_d_to_ar_ad': '3D Model to AR Ad Generation [Credit-Based]',
      'video_generation': 'Image to Video Generation [Credit-Based]',

      'post_scheduling': 'Automated Post Scheduling',
      'media_storage_gb': 'Media Storage (GB)'
    };
    return names[featureKey] || featureKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
      <div className="grid gap-4">
        {Object.entries(limits).map(([featureKey, limit]) => (
          <div key={featureKey} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  {getFeatureIcon(featureKey)}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
                    {getFeatureDisplayName(featureKey)}
                  </h4>
                </div>
              </div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={limit.is_active}
                  onChange={(e) => updateLimit(featureKey, 'is_active', e.target.checked)}
                  className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500/30 w-4 h-4"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">Enabled</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor={`${featureKey}_limit`} className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  Limit Value
                </Label>
                <Input
                  id={`${featureKey}_limit`}
                  type="text"
                  value={limit.limit_value.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
                      const numValue = value === '' || value === '-' ? 0 : parseInt(value);
                      updateLimit(featureKey, 'limit_value', numValue);
                    }
                  }}
                  placeholder="-1 for unlimited"
                  disabled={!limit.is_active}
                  className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/30 text-sm"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor={`${featureKey}_period`} className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  Reset Period
                </Label>
                <select
                  id={`${featureKey}_period`}
                  value={limit.reset_period}
                  onChange={(e) => updateLimit(featureKey, 'reset_period', e.target.value)}
                  disabled={!limit.is_active}
                  className="w-full h-9 px-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:border-blue-500 focus:ring-blue-500/30 text-slate-800 dark:text-slate-200 text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="daily">Daily</option>
                  <option value="never">Never</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-200/50 dark:border-slate-700/50 pt-4 -mx-2 px-2">
        <Button
          onClick={handleSave}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white h-10 rounded-lg shadow-md hover:shadow-lg transition-all font-medium"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              <span>Saving...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Save className="w-4 h-4" />
              <span>Save Configuration</span>
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

const TierManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [selectedTierForLimits, setSelectedTierForLimits] = useState<Tier | null>(null);
  const [isLimitsDialogOpen, setIsLimitsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'limits' | 'pricing'>('limits');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<Tier | null>(null);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<TierCreate>();

  // Real API calls
  const { data: tiers, isLoading, error } = useQuery<Tier[]>({
    queryKey: ['tiers'],
    queryFn: () => tierService.getAllTiers(false), // Get all tiers (active and inactive)
  });

  // Tier limits query
  const { data: tierLimits, isLoading: limitsLoading, refetch: refetchTierLimits } = useQuery<TierLimit[]>({
    queryKey: ['tier-limits', selectedTierForLimits?.id],
    queryFn: () => selectedTierForLimits ? tierService.getTierLimits(selectedTierForLimits.id) : Promise.resolve([]),
    enabled: !!selectedTierForLimits,
    staleTime: 0, // Always consider data stale
    cacheTime: 0, // Don't cache the data
  });

  const createMutation = useMutation({
    mutationFn: tierService.createTier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiers'] });
      queryClient.invalidateQueries({ queryKey: ['public-tiers'] }); // Invalidate pricing page cache
      toast.success('Tier created successfully!');
      setIsFormOpen(false);
      reset();
    },
    onError: (err: any) => {
      console.error('Create tier error:', err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Unknown error occurred';
      toast.error(`Error creating tier: ${errorMessage}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ tierId, data }: { tierId: string; data: any }) =>
      tierService.updateTier(tierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiers'] });
      queryClient.invalidateQueries({ queryKey: ['public-tiers'] }); // Invalidate pricing page cache
      toast.success('Tier updated successfully!');
      setIsFormOpen(false);
      setEditingTier(null);
      reset();
    },
    onError: (err: any) => {
      console.error('Update tier error:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);

      let errorMessage = 'Unknown error occurred';

      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          } else {
            errorMessage = err.response.data.detail;
          }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error(`Error updating tier: ${errorMessage}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tierService.deleteTier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiers'] });
      queryClient.invalidateQueries({ queryKey: ['public-tiers'] }); // Invalidate pricing page cache
      toast.success('Tier deleted successfully!');
    },
    onError: (err: any) => {
      toast.error(`Error deleting tier: ${err.response?.data?.detail || err.message}`);
    },
  });

  const bulkUpdateLimitsMutation = useMutation({
    mutationFn: tierService.bulkUpdateTierLimits,
    onSuccess: () => {
      // Invalidate all tier-limits queries
      queryClient.invalidateQueries({ queryKey: ['tier-limits'] });
      toast.success('Tier limits updated successfully!');
      // Don't close the dialog immediately, let the user see the updated values
      setTimeout(() => setIsLimitsDialogOpen(false), 1000);
    },
    onError: (err: any) => {
      toast.error(`Error updating limits: ${err.response?.data?.detail || err.message}`);
    },
  });

  const onSubmit = (data: TierCreate) => {
    console.log('Form data being submitted:', data);
    if (editingTier) {
      console.log('Updating tier:', editingTier.id);
      // For updates, exclude the 'name' field and only send updatable fields
      const updateData: TierUpdate = {
        display_name: data.display_name,
        description: data.description || null,
        price_monthly: data.price_monthly || null,
        price_yearly: data.price_yearly || null,
        signup_credits: data.signup_credits,
        monthly_credits: data.monthly_credits,
        bonus_credits: data.bonus_credits || 0,
        bonus_start_date: data.bonus_start_date || null,
        bonus_end_date: data.bonus_end_date || null,
        bonus_description: data.bonus_description || null,
        bonus_is_active: data.bonus_is_active || false,
        sort_order: data.sort_order,
        button_text: data.button_text || null,
        button_url: data.button_url || null,
        is_button_enabled: data.is_button_enabled,
        icon: data.icon || null,
        roi_text_1: data.roi_text_1 || null,
        roi_text_2: data.roi_text_2 || null,
        show_roi_texts: data.show_roi_texts || false
      };
      console.log('Update data being sent:', updateData);
      updateMutation.mutate({ tierId: editingTier.id, data: updateData });
    } else {
      console.log('Creating new tier');
      createMutation.mutate(data);
    }
  };

  const handleEditClick = (tier: Tier) => {
    setEditingTier(tier);
    setValue('name', tier.name);
    setValue('display_name', tier.display_name);
    setValue('description', tier.description || '');
    setValue('price_monthly', tier.price_monthly || 0);
    setValue('price_yearly', tier.price_yearly || 0);
    setValue('signup_credits', tier.signup_credits);
    setValue('monthly_credits', tier.monthly_credits);
    setValue('bonus_credits', tier.bonus_credits || 0);
    setValue('bonus_start_date', tier.bonus_start_date ? tier.bonus_start_date.slice(0, 16) : '');
    setValue('bonus_end_date', tier.bonus_end_date ? tier.bonus_end_date.slice(0, 16) : '');
    setValue('bonus_description', tier.bonus_description || '');
    setValue('bonus_is_active', tier.bonus_is_active || false);
    setValue('sort_order', tier.sort_order);
    setValue('button_text', tier.button_text || '');
    setValue('button_url', tier.button_url || '');
    setValue('is_button_enabled', tier.is_button_enabled);
    setValue('icon', tier.icon || 'Users');
    setValue('roi_text_1', tier.roi_text_1 || '');
    setValue('roi_text_2', tier.roi_text_2 || '');
    setValue('show_roi_texts', tier.show_roi_texts || false);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (tier: Tier) => {
    setTierToDelete(tier);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (tierToDelete) {
      deleteMutation.mutate(tierToDelete.id);
      setIsDeleteDialogOpen(false);
      setTierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setTierToDelete(null);
  };

  const handleNewTierClick = () => {
    setEditingTier(null);
    reset();
    // Set default values for new tier
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextMonth = new Date(now);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setValue('sort_order', 1);
    setValue('signup_credits', 10);
    setValue('monthly_credits', 5);
    setValue('bonus_credits', 0);
    setValue('bonus_is_active', false);
    setValue('is_button_enabled', true);
    setValue('icon', 'Users');
    setValue('roi_text_1', '');
    setValue('roi_text_2', '');
    setValue('show_roi_texts', false);
    // Set default bonus dates to today and next month
    setValue('bonus_start_date', now.toISOString().slice(0, 16));
    setValue('bonus_end_date', nextMonth.toISOString().slice(0, 16));

    setIsFormOpen(true);
  };

  const handleManageLimitsClick = (tier: Tier) => {
    setSelectedTierForLimits(tier);
    setIsLimitsDialogOpen(true);
    // Force refetch when dialog opens
    setTimeout(() => {
      refetchTierLimits();
    }, 100);
  };

  // Add default social media limits to the tier limits form
  const defaultSocialLimits = [
    { key: 'connected_accounts', name: 'Connected Social Accounts', defaultLimit: 2 },
    { key: 'monthly_posts', name: 'Monthly Posts Published', defaultLimit: 10 },
    { key: 'scheduled_queue', name: 'Scheduled Posts Queue', defaultLimit: 5 },
    { key: 'team_members', name: 'Team Members', defaultLimit: 1 },
    { key: 'storage_gb', name: 'Media Storage (GB)', defaultLimit: 1 },
    { key: 'analytics_days', name: 'Analytics History (Days)', defaultLimit: 30 }
  ];

  const handleUpdateLimits = (limits: Record<string, { limit_value: number; reset_period: string; is_active: boolean }>) => {
    if (!selectedTierForLimits) return;

    bulkUpdateLimitsMutation.mutate({
      tier_id: selectedTierForLimits.id,
      limits
    });
  };

  const formatPrice = (price?: number) => {
    if (!price || price === 0) return 'Free';
    return `$${price.toFixed(2)}`;
  };

  if (isLoading) {
    return <div className="container mx-auto p-6">Loading tiers...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Error Loading Tiers
          </h2>
          <p className="text-red-700 dark:text-red-300 mb-4">
            {error?.message || 'Failed to load tier data'}
          </p>

          <div className="bg-red-100 dark:bg-red-900/40 rounded-lg p-4">
            <h3 className="font-medium text-red-800 dark:text-red-200 mb-2">Possible Solutions:</h3>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
              <li>• Check if database migrations have been applied</li>
              <li>• Verify the database connection is working</li>
              <li>• Ensure tier tables exist in the database</li>
              <li>• Check server logs for detailed error information</li>
              <li>• Verify authentication is working</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      {/* Modern Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-gray-900 via-blue-600 to-purple-600 bg-clip-text text-transparent dark:from-white dark:via-blue-300 dark:to-purple-300">
                  Tier Management
                </h1>
                <p className="text-slate-600 dark:text-slate-300">Manage subscription tiers and feature limits</p>
              </div>
            </div>

            <Button
              onClick={handleNewTierClick}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
            >
              <PlusCircle className="mr-2 h-5 w-5" />
              Create New Tier
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {tiers && (
          <motion.div
            className="relative overflow-hidden rounded-3xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-indigo-50/30 to-purple-50/30 dark:from-indigo-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>

            {/* Table Header */}
            <div className="relative border-b border-slate-200/60 dark:border-slate-600/40 px-6 py-4">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-3 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  Tier Details
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Pricing
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Credits
                </div>
                <div className="col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-center">
                  Status
                </div>
                <div className="col-span-3 text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider text-right">
                  Actions
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="relative">
              {tiers.map((tier, index) => {
                const getTierIcon = (tier: Tier) => {
                  // Use the tier's icon field if available, otherwise fall back to name-based logic
                  const iconName = tier.icon || 'Users';

                  switch (iconName) {
                    case 'Crown':
                      return { Icon: Crown, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10' };
                    case 'Zap':
                      return { Icon: Zap, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' };
                    case 'Coins':
                      return { Icon: Coins, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10' };
                    case 'Wand2':
                      return { Icon: Wand2, color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10' };
                    case 'Layers':
                      return { Icon: Layers, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' };
                    case 'Box':
                      return { Icon: Box, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-500/10' };
                    case 'Video':
                      return { Icon: Video, color: 'text-pink-600 dark:text-pink-400', bgColor: 'bg-pink-500/10' };
                    case 'HardDrive':
                      return { Icon: HardDrive, color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-500/10' };
                    case 'Users':
                    default:
                      return { Icon: Users, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10' };
                  }
                };

                const { Icon, color, bgColor } = getTierIcon(tier);

                return (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group relative border-b border-slate-200/40 dark:border-slate-600/30 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-all duration-200"
                  >
                    <div className="px-6 py-5">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        {/* Tier Details */}
                        <div className="col-span-3 flex items-center space-x-4">
                          <div className={`w-10 h-10 ${bgColor} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200`}>
                            <Icon className={`w-5 h-5 ${color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
                              {tier.display_name}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                              {tier.description || tier.name}
                            </p>
                          </div>
                        </div>

                        {/* Pricing */}
                        <div className="col-span-2 text-center">
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              ${formatPrice(tier.price_monthly)}/mo
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              ${formatPrice(tier.price_yearly)}/yr
                            </div>
                          </div>
                        </div>

                        {/* Credits */}
                        <div className="col-span-2 text-center">
                          <div className="space-y-1">
                            <div className="flex items-center justify-center space-x-1">
                              <Coins className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                              <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                                {tier.signup_credits}
                              </span>
                              {tier.bonus_is_active && tier.bonus_credits > 0 &&
                                tier.bonus_start_date && tier.bonus_end_date &&
                                new Date() >= new Date(tier.bonus_start_date) &&
                                new Date() <= new Date(tier.bonus_end_date) && (
                                  <>
                                    <span className="text-xs text-slate-400">+</span>
                                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                      {tier.bonus_credits}
                                    </span>
                                  </>
                                )}
                              <span className="text-xs text-slate-500 dark:text-slate-400">signup</span>
                            </div>
                            <div className="flex items-center justify-center space-x-1">
                              <Coins className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                              <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                {tier.monthly_credits}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">monthly</span>
                            </div>
                            {tier.bonus_is_active && tier.bonus_credits > 0 &&
                              tier.bonus_start_date && tier.bonus_end_date &&
                              new Date() >= new Date(tier.bonus_start_date) &&
                              new Date() <= new Date(tier.bonus_end_date) && (
                                <div className="flex items-center justify-center space-x-1">
                                  <Gift className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                                  <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                                    {tier.bonus_description || 'Bonus Active'}
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Status */}
                        <div className="col-span-2 flex justify-center">
                          <div className="text-center">
                            <span className={`px-3 py-1 rounded-xl text-xs font-semibold ${tier.is_active
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                              }`}>
                              {tier.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {tier.user_count || 0} users
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="col-span-3 flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(tier)}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-blue-100 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-105"
                            title="Edit Tier"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleManageLimitsClick(tier)}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-purple-100 dark:hover:bg-purple-900/20 hover:text-purple-600 dark:hover:text-purple-400 hover:scale-105"
                            title="Configure Tier Features"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(tier)}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:scale-105"
                            title="Delete Tier"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Monthly Credit Management Section */}
        <motion.div
          className="relative overflow-hidden rounded-3xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-2xl mt-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-indigo-50/30 to-purple-50/30 dark:from-indigo-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>

          <div className="relative p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Monthly Credit Management</h2>
                  <p className="text-slate-600 dark:text-slate-300">Manage automated monthly credit granting</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Manual Credit Grant */}
              <Card className="border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Gift className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Grant Credits</h3>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    Emergency override: Manually trigger credit granting (system runs automatically)
                  </p>
                  <Button
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={() => {
                      // TODO: Implement manual credit granting
                      toast.success('Monthly credit granting queued!');
                    }}
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    Grant Monthly Credits
                  </Button>
                </CardContent>
              </Card>

              {/* Usage Reset */}
              <Card className="border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mr-3">
                      <RefreshCw className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Reset Usage</h3>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    Emergency override: Manually reset usage counters (system runs automatically)
                  </p>
                  <Button
                    variant="outline"
                    className="w-full border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                    onClick={() => {
                      // TODO: Implement usage reset
                      toast.success('Monthly usage reset queued!');
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Monthly Usage
                  </Button>
                </CardContent>
              </Card>

              {/* System Status */}
              <Card className="border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">System Status</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Grant Type:</span>
                      <span className="font-medium text-slate-800 dark:text-slate-200">Anniversary-Based</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Schedule:</span>
                      <span className="font-medium text-slate-800 dark:text-slate-200">Daily at 00:00 UTC</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Status:</span>
                      <span className="font-medium text-green-600 dark:text-green-400">Active</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Anniversary-Based Credit System</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Fully Automated:</strong> Credits are granted based on each user's signup anniversary (30+ days after signup or last grant).
                    The system runs daily at midnight UTC to check for eligible users. Usage counters are reset 30 minutes later.
                    <strong>No manual intervention required.</strong> Manual buttons above are for emergency/testing use only.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Monthly Credit Schedule Control */}
        <div className="mt-8">
          <CreditScheduleControl />
        </div>

        {/* Create/Edit Tier Dialog - Streamlined & Professional */}
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white/98 dark:bg-slate-900/98 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 shadow-2xl rounded-2xl">
            <DialogHeader className="space-y-3 pb-4">
              <DialogTitle className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  {editingTier ? <Edit className="w-4 h-4 text-white" /> : <PlusCircle className="w-4 h-4 text-white" />}
                </div>
                <span className="text-xl font-bold text-slate-800 dark:text-slate-100">
                  {editingTier ? 'Edit Tier' : 'Create Tier'}
                </span>
              </DialogTitle>
              <DialogDescription className="text-slate-600 dark:text-slate-400">
                {editingTier ? 'Update tier configuration' : 'Configure a new subscription tier'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Compact Form Layout */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Internal Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="basic, pro, enterprise"
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  />
                  {errors.name && <p className="text-red-500 text-xs">Required</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display_name" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Display Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="display_name"
                    {...register('display_name', { required: 'Display name is required' })}
                    placeholder="Basic Plan, Pro Plan"
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  />
                  {errors.display_name && <p className="text-red-500 text-xs">Required</p>}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="description" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Description
                </Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Brief description of tier benefits"
                  rows={2}
                  className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none"
                />
              </div>

              {/* Icon Selector */}
              <div className="space-y-2">
                <Label htmlFor="icon" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Icon
                </Label>
                <Select onValueChange={(value) => setValue('icon', value)} value={watch('icon') || 'Users'}>
                  <SelectTrigger className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all">
                    <SelectValue placeholder="Choose an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Users">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4" />
                        <span>Users (Free/Basic)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Zap">
                      <div className="flex items-center space-x-2">
                        <Zap className="w-4 h-4" />
                        <span>Zap (Pro/Power)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Crown">
                      <div className="flex items-center space-x-2">
                        <Crown className="w-4 h-4" />
                        <span>Crown (Premium/Enterprise)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Coins">
                      <div className="flex items-center space-x-2">
                        <Coins className="w-4 h-4" />
                        <span>Coins (Credits/Value)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Wand2">
                      <div className="flex items-center space-x-2">
                        <Wand2 className="w-4 h-4" />
                        <span>Magic Wand (AI/Creative)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Layers">
                      <div className="flex items-center space-x-2">
                        <Layers className="w-4 h-4" />
                        <span>Layers (Advanced)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Box">
                      <div className="flex items-center space-x-2">
                        <Box className="w-4 h-4" />
                        <span>Box (Package)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Video">
                      <div className="flex items-center space-x-2">
                        <Video className="w-4 h-4" />
                        <span>Video (Media)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="HardDrive">
                      <div className="flex items-center space-x-2">
                        <HardDrive className="w-4 h-4" />
                        <span>Storage (Data)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pricing Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_monthly" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Monthly Price ($)
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-sm">$</div>
                    <Input
                      id="price_monthly"
                      type="number"
                      step="0.01"
                      {...register('price_monthly', { valueAsNumber: true })}
                      placeholder="29.99"
                      className="h-10 pl-7 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price_yearly" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Yearly Price ($)
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 text-sm">$</div>
                    <Input
                      id="price_yearly"
                      type="number"
                      step="0.01"
                      {...register('price_yearly', { valueAsNumber: true })}
                      placeholder="299.99"
                      className="h-10 pl-7 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Credits & Order Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="signup_credits" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Signup Credits <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="signup_credits"
                    type="number"
                    {...register('signup_credits', { required: true, valueAsNumber: true })}
                    placeholder="10"
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthly_credits" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Monthly Credits <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="monthly_credits"
                    type="number"
                    {...register('monthly_credits', { required: true, valueAsNumber: true })}
                    placeholder="50"
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort_order" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Sort Order <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="sort_order"
                    type="number"
                    {...register('sort_order', { required: true, valueAsNumber: true })}
                    placeholder="1"
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all"
                  />
                </div>
              </div>

              {/* Bonus Credits Section */}
              <div className="space-y-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <Gift className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Bonus Credits Campaign</h3>
                </div>

                {/* Bonus Toggle and Credits */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <input
                        id="bonus_is_active"
                        type="checkbox"
                        {...register('bonus_is_active')}
                        className="w-4 h-4 text-orange-600 bg-white border-slate-300 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <Label htmlFor="bonus_is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Enable Bonus Credits
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bonus_credits" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Bonus Credits Amount
                    </Label>
                    <Input
                      id="bonus_credits"
                      type="number"
                      {...register('bonus_credits', { valueAsNumber: true })}
                      placeholder="15"
                      disabled={!watch('bonus_is_active')}
                      className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Bonus Description */}
                <div className="space-y-2">
                  <Label htmlFor="bonus_description" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Bonus Description
                  </Label>
                  <Input
                    id="bonus_description"
                    {...register('bonus_description')}
                    placeholder="Launch Special, Black Friday Bonus, etc."
                    disabled={!watch('bonus_is_active')}
                    className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all disabled:opacity-50"
                  />
                </div>

                {/* Bonus Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bonus_start_date" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Start Date
                    </Label>
                    <Input
                      id="bonus_start_date"
                      type="datetime-local"
                      {...register('bonus_start_date')}
                      disabled={!watch('bonus_is_active')}
                      className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all disabled:opacity-50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bonus_end_date" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      End Date
                    </Label>
                    <Input
                      id="bonus_end_date"
                      type="datetime-local"
                      {...register('bonus_end_date')}
                      disabled={!watch('bonus_is_active')}
                      className="h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Bonus Preview */}
                {watch('bonus_is_active') && watch('bonus_credits') > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Gift className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <span className="text-sm font-semibold text-orange-800 dark:text-orange-200">Bonus Preview</span>
                    </div>
                    <div className="text-sm text-orange-700 dark:text-orange-300">
                      Users will receive <strong>{watch('signup_credits') || 0} + {watch('bonus_credits') || 0} = {(watch('signup_credits') || 0) + (watch('bonus_credits') || 0)} credits</strong> when they sign up
                      {watch('bonus_description') && <span> for "{watch('bonus_description')}"</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Button Customization Section - Compact */}
              <div className="space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <Code className="w-3 h-3 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Button Settings</h3>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="button_text" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Button Text
                    </Label>
                    <Input
                      id="button_text"
                      {...register('button_text')}
                      placeholder="Get Started"
                      className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="button_action" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Button Action
                    </Label>
                    <Select
                      value={watch('button_url') || '/register'}
                      onValueChange={(value) => setValue('button_url', value)}
                    >
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500">
                        <SelectValue placeholder="Select action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="/register">
                          <div className="flex items-center space-x-2">
                            <span>📝</span>
                            <span>Register Page</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="/roadmap">
                          <div className="flex items-center space-x-2">
                            <span>🗺️</span>
                            <span>Roadmap Page</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="waitlist">
                          <div className="flex items-center space-x-2">
                            <span>⏳</span>
                            <span>Join Waitlist</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="disabled">
                          <div className="flex items-center space-x-2">
                            <span>🚫</span>
                            <span>Disabled (Not Clickable)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="is_button_enabled"
                    type="checkbox"
                    {...register('is_button_enabled')}
                    className="w-3 h-3 text-orange-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded focus:ring-orange-500/30 focus:ring-1"
                  />
                  <Label htmlFor="is_button_enabled" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Show button (uncheck to hide button entirely)
                  </Label>
                </div>
              </div>

              {/* ROI Texts Section */}
              <div className="space-y-3 p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-xl border border-green-200/30 dark:border-green-700/30">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-3 h-3 text-white" />
                  </div>
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-300">ROI Messages</h4>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="show_roi_texts"
                    type="checkbox"
                    {...register('show_roi_texts')}
                    className="w-3 h-3 text-green-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded focus:ring-green-500/30 focus:ring-1"
                  />
                  <Label htmlFor="show_roi_texts" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Show ROI messages on pricing cards
                  </Label>
                </div>

                {watch('show_roi_texts') && (
                  <div className="space-y-3 mt-3">
                    <div className="space-y-1">
                      <Label htmlFor="roi_text_1" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        ROI Message 1
                      </Label>
                      <Input
                        id="roi_text_1"
                        {...register('roi_text_1')}
                        placeholder="Save $200/month vs hiring designers"
                        className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="roi_text_2" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        ROI Message 2
                      </Label>
                      <Input
                        id="roi_text_2"
                        {...register('roi_text_2')}
                        placeholder="Save $2,000/month in content costs"
                        className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="pt-4 border-t border-slate-200/50 dark:border-slate-700/50 flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsFormOpen(false)}
                  className="flex-1 h-10 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg font-medium transition-all"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 h-10 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all font-medium disabled:opacity-70"
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>{editingTier ? 'Saving...' : 'Creating...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="w-4 h-4" />
                      <span>{editingTier ? 'Save' : 'Create'}</span>
                    </div>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tier Configuration Dialog - Streamlined & Professional */}
        <Dialog open={isLimitsDialogOpen} onOpenChange={setIsLimitsDialogOpen}>
          <DialogContent className="sm:max-w-[900px] max-h-[85vh] bg-white/98 dark:bg-slate-900/98 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 shadow-2xl rounded-2xl">
            <DialogHeader className="space-y-3 pb-4 border-b border-slate-200/40 dark:border-slate-700/40">
              <DialogTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Settings className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                      {selectedTierForLimits?.display_name} Configuration
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Manage tier settings and features
                    </p>
                  </div>
                </div>
              </DialogTitle>

              {/* Compact Tab Navigation */}
              <div className="flex space-x-1 bg-slate-100/80 dark:bg-slate-800/80 p-1 rounded-lg backdrop-blur-sm">
                <button
                  onClick={() => setActiveTab('limits')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-150 ${activeTab === 'limits'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                >
                  <Settings className="w-3 h-3 inline mr-1.5" />
                  Usage Limits
                </button>
                <button
                  onClick={() => setActiveTab('pricing')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-150 ${activeTab === 'pricing'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                    }`}
                >
                  <Tag className="w-3 h-3 inline mr-1.5" />
                  Pricing Features
                </button>
              </div>
            </DialogHeader>

            {/* Streamlined Tab Content */}
            <div className="py-4 overflow-hidden h-[70vh] flex flex-col">
              {activeTab === 'limits' ? (
                limitsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-3"></div>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">Loading configuration...</p>
                    </div>
                  </div>
                ) : (
                  <TierLimitsForm
                    tierLimits={tierLimits || []}
                    onSave={handleUpdateLimits}
                    isLoading={bulkUpdateLimitsMutation.isPending}
                  />
                )
              ) : (
                selectedTierForLimits && (
                  <TierPricingFeaturesManager
                    tierId={selectedTierForLimits.id}
                    tierName={selectedTierForLimits.display_name}
                  />
                )
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[400px] bg-white/98 dark:bg-slate-900/98 backdrop-blur-xl border border-red-200/60 dark:border-red-700/60 shadow-2xl rounded-2xl">
            <DialogHeader className="space-y-3 pb-4">
              <DialogTitle className="text-xl font-bold text-red-600 dark:text-red-400 flex items-center space-x-2">
                <Trash2 className="w-5 h-5" />
                <span>Delete Tier</span>
              </DialogTitle>
              <DialogDescription className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Are you sure you want to delete the <strong>"{tierToDelete?.display_name}"</strong> tier? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col space-y-3">
              {tierToDelete?.user_count && tierToDelete.user_count > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Warning: This tier has {tierToDelete.user_count} active user{tierToDelete.user_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={cancelDelete}
                className="flex-1 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Tier
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit Package Management Section */}
        <div className="mt-8">
          <CreditPackageManager />
        </div>
      </div>
    </div>
  );
};

export default TierManagementPage;